class InternalVideo extends HTMLElement {
  constructor() {
    super();
    this.sourcesLoaded = false;
    this.dragging = false;
    this.autoplayAttempted = false;
    this.observer = null;

    this._onPlayClick = this._onPlayClick.bind(this);
    this._onSoundClick = this._onSoundClick.bind(this);
    this._onFullscreenClick = this._onFullscreenClick.bind(this);
    this._onFullscreenChange = this._onFullscreenChange.bind(this);
    this._onTimeUpdate = this._onTimeUpdate.bind(this);
    this._onTimelineClick = this._onTimelineClick.bind(this);
    this._onTimelineStartDrag = this._onTimelineStartDrag.bind(this);
    this._onTimelineStopDrag = this._onTimelineStopDrag.bind(this);
    this._onTimelineDrag = this._onTimelineDrag.bind(this);
    this._onWaiting = this._onWaiting.bind(this);
    this._onCanPlay = this._onCanPlay.bind(this);
    this._onPlaying = this._onPlaying.bind(this);
    this._onEnded = this._onEnded.bind(this);
    this._onIntersect = this._onIntersect.bind(this);
    this._onSectionLoad = this._onSectionLoad.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;

    this.video = this.querySelector('video');
    if (!this.video) return;

    this._initialized = true;
    this.playButton = this.querySelector('.internal-video__play');
    this.playButtonIcon = this.querySelector('.play-button');
    this.soundButton = this.querySelector('.internal-video__sound-btn');
    this.fullscreenButton = this.querySelector('.internal-video__fullscreen-btn');
    this.timeline = this.querySelector('.internal-video__timeline');
    this.noPlayBtn = this.dataset.noPlayBtn === 'true';
    this.shouldAutoplay = this.dataset.autoplay === 'true';

    this._removeInvalidSources();
    this._hidePlayIconIfNeeded();

    if (this.playButton) {
      this.playButton.addEventListener('click', this._onPlayClick);
    }

    if (this.soundButton) {
      this.soundButton.addEventListener('click', this._onSoundClick);
    }

    if (this.fullscreenButton) {
      this.fullscreenButton.addEventListener('click', this._onFullscreenClick);
      document.addEventListener('fullscreenchange', this._onFullscreenChange);
    }

    if (this.timeline) {
      this.video.addEventListener('timeupdate', this._onTimeUpdate);
      this.timeline.addEventListener('click', this._onTimelineClick);
      this.timeline.addEventListener('mousedown', this._onTimelineStartDrag);
      this.timeline.addEventListener('touchstart', this._onTimelineStartDrag, { passive: false });
      document.addEventListener('mouseup', this._onTimelineStopDrag);
      document.addEventListener('touchend', this._onTimelineStopDrag);
      document.addEventListener('mousemove', this._onTimelineDrag);
      document.addEventListener('touchmove', this._onTimelineDrag, { passive: false });
    }

    this.video.addEventListener('waiting', this._onWaiting);
    this.video.addEventListener('canplay', this._onCanPlay);
    this.video.addEventListener('playing', this._onPlaying);
    this.video.addEventListener('ended', this._onEnded);

    if (this.shouldAutoplay) {
      this._setupAutoplay();
    }

    document.addEventListener('shopify:section:load', this._onSectionLoad);
    window.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback() {
    this._initialized = false;
    this.autoplayAttempted = false;

    if (this.playButton) {
      this.playButton.removeEventListener('click', this._onPlayClick);
    }

    if (this.soundButton) {
      this.soundButton.removeEventListener('click', this._onSoundClick);
    }

    if (this.fullscreenButton) {
      this.fullscreenButton.removeEventListener('click', this._onFullscreenClick);
      document.removeEventListener('fullscreenchange', this._onFullscreenChange);
    }

    if (this.timeline) {
      this.video?.removeEventListener('timeupdate', this._onTimeUpdate);
      this.timeline.removeEventListener('click', this._onTimelineClick);
      this.timeline.removeEventListener('mousedown', this._onTimelineStartDrag);
      this.timeline.removeEventListener('touchstart', this._onTimelineStartDrag);
      document.removeEventListener('mouseup', this._onTimelineStopDrag);
      document.removeEventListener('touchend', this._onTimelineStopDrag);
      document.removeEventListener('mousemove', this._onTimelineDrag);
      document.removeEventListener('touchmove', this._onTimelineDrag);
    }

    this.video?.removeEventListener('waiting', this._onWaiting);
    this.video?.removeEventListener('canplay', this._onCanPlay);
    this.video?.removeEventListener('playing', this._onPlaying);
    this.video?.removeEventListener('ended', this._onEnded);

    this.observer?.disconnect();
    this.observer = null;

    document.removeEventListener('shopify:section:load', this._onSectionLoad);
    window.removeEventListener('keydown', this._onKeyDown);
    if (this.classList.contains('internal-video--modal-fullscreen')) {
      document.body.style.overflow = '';
    }
  }

  _onSectionLoad(event) {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const sectionId = section.id?.replace('shopify-section-', '');
    if (event.detail?.sectionId !== sectionId) return;

    if (this.shouldAutoplay && this.video?.paused) {
      this.autoplayAttempted = false;
      this._tryAutoplay();
    }
  }

  _setupAutoplay() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this._onIntersect, {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      });
      this.observer.observe(this);
    }

    requestAnimationFrame(() => {
      this._tryAutoplay();
    });
  }

  _isInViewport() {
    const rect = this.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
  }

  _hidePlayIconIfNeeded() {
    if (this.playButtonIcon && this.noPlayBtn) {
      this.playButtonIcon.style.visibility = 'hidden';
    }
  }

  _showPlayIcon() {
    if (this.playButtonIcon && this.noPlayBtn) {
      this.playButtonIcon.style.visibility = 'visible';
    }
  }

  _removeInvalidSources() {
    const invalidSource = this.video.querySelector('source[src=""]');
    if (invalidSource) {
      invalidSource.remove();
    }
  }

  _loadSources() {
    const lazySources = this.video.querySelectorAll('source[data-src]');

    for (const source of lazySources) {
      source.setAttribute('src', source.getAttribute('data-src'));
      source.removeAttribute('data-src');
    }

    if (lazySources.length > 0 || !this.sourcesLoaded) {
      this.video.load();
    }

    this.sourcesLoaded = true;
  }

  _setPlayingState(isPlaying) {
    if (isPlaying) {
      this.classList.add('internal-video--playing');
      this._hidePlayIconIfNeeded();
      return;
    }

    this.classList.remove('internal-video--playing');
    this._showPlayIcon();
  }

  _playVideo() {
    if (!this.video) return;

    if (this.video.paused) {
      this._loadSources();
      this._setPlayingState(true);

      const playPromise = this.video.play();

      if (!playPromise) return;

      playPromise
        .then(() => {
          this._setPlayingState(true);
        })
        .catch(() => {
          this._setPlayingState(false);
        });
    } else {
      this.video.pause();
      this._setPlayingState(false);
    }
  }

  _tryAutoplay() {
    if (!this.shouldAutoplay || this.autoplayAttempted || !this.video) return;
    const isDesignMode = window.Shopify && window.Shopify.designMode;
    if (!isDesignMode && !this._isInViewport()) return;

    this.autoplayAttempted = true;
    this._loadSources();

    const playPromise = this.video.play();

    if (!playPromise) {
      this.observer?.disconnect();
      return;
    }

    playPromise
      .then(() => {
        this._setPlayingState(true);
        this.observer?.disconnect();
      })
      .catch(() => {
        this.autoplayAttempted = false;
        this._setPlayingState(false);
      });
  }

  _onPlayClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this._playVideo();
  }

  _onSoundClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.video) return;

    if (this.video.muted) {
      this.video.muted = false;
      this.classList.remove('internal-video--muted');
    } else {
      this.video.muted = true;
      this.classList.add('internal-video--muted');
    }
  }

  _onFullscreenClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const isModalFullscreen = this.classList.contains('internal-video--modal-fullscreen');

    if (isModalFullscreen) {
      this.classList.remove('internal-video--modal-fullscreen');
      this.classList.remove('internal-video--fullscreen');
      document.body.style.overflow = '';
    } else {
      this.classList.add('internal-video--modal-fullscreen');
      this.classList.add('internal-video--fullscreen');
      document.body.style.overflow = 'hidden';
    }
  }

  _enterVideoFullscreen() {
    if (this.video?.webkitEnterFullscreen) {
      this.video.webkitEnterFullscreen();
      return;
    }

    if (this.video?.requestFullscreen) {
      this.video.requestFullscreen().catch(() => {});
    }
  }

  _onFullscreenChange() {
    if (document.fullscreenElement === this || document.fullscreenElement === this.video) {
      this.classList.add('internal-video--fullscreen');
      return;
    }

    this.classList.remove('internal-video--fullscreen');
  }

  _onKeyDown(event) {
    if (event.key === 'Escape' && this.classList.contains('internal-video--modal-fullscreen')) {
      this.classList.remove('internal-video--modal-fullscreen');
      this.classList.remove('internal-video--fullscreen');
      document.body.style.overflow = '';
    }
  }

  _onTimeUpdate() {
    if (!this.timeline || !this.video || !this.video.duration) return;

    const completed = (this.video.currentTime / this.video.duration) * 100;
    this.timeline.style.setProperty('--completed', `${completed}%`);
  }

  _seekToPosition(clientX) {
    if (!this.timeline || !this.video || !this.video.duration) return;

    const rect = this.timeline.getBoundingClientRect();
    const position = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    this.video.currentTime = position * this.video.duration;
  }

  _onTimelineClick(event) {
    event.stopPropagation();
    this._seekToPosition(event.clientX);
  }

  _onTimelineStartDrag(event) {
    event.preventDefault();
    event.stopPropagation();
    this.dragging = true;
    this._seekToPosition(event.touches ? event.touches[0].clientX : event.clientX);
  }

  _onTimelineStopDrag() {
    this.dragging = false;
  }

  _onTimelineDrag(event) {
    if (!this.dragging) return;

    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    this._seekToPosition(clientX);
  }

  _onWaiting() {
    this.classList.add('internal-video--loading');
  }

  _onCanPlay() {
    this.classList.remove('internal-video--loading');
  }

  _onPlaying() {
    this.classList.remove('internal-video--loading');
    this._setPlayingState(true);
  }

  _onEnded() {
    this._setPlayingState(false);
  }

  _onIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      this._tryAutoplay();
    });
  }
}

if (!customElements.get('internal-video')) {
  customElements.define('internal-video', InternalVideo);
}
